/**
 * TanStack Query setup and the supabase-js read hooks.
 *
 * Query keys start with 'seo' and the table name: ['seo', 'opportunities', ...]. After a write,
 * `queryClient.invalidateQueries({ queryKey: ['seo', '<table>'] })` refreshes every reader of
 * that table, including the shell (sidebar badge, spend meter, last run).
 *
 * Two error codes switch the whole app to a setup screen (see App.tsx):
 *   PGRST106  schema `seo` is not in the API's exposed schemas → SchemaNotExposed
 *   42501     the database refused the read for this account   → NotAllowed
 */
import { useSyncExternalStore } from 'react';
import { MutationCache, QueryCache, QueryClient, useQuery, type UseQueryResult } from '@tanstack/react-query';
import { ApiError, callApi } from './api';

/** A PostgREST or Postgres error, thrown by `useSb` and `unwrap`. */
export class SbError extends Error {
  readonly code: string;
  readonly details: string | null;
  readonly hint: string | null;
  constructor(e: { message?: string; code?: string; details?: string | null; hint?: string | null }) {
    super(e.message || 'The database request failed.');
    this.name = 'SbError';
    this.code = e.code ?? '';
    this.details = e.details ?? null;
    this.hint = e.hint ?? null;
  }
}

export function toSbError(e: unknown): SbError {
  if (e instanceof SbError) return e;
  if (e && typeof e === 'object') return new SbError(e as Record<string, string>);
  return new SbError({ message: String(e) });
}

// ---- App-wide gate --------------------------------------------------------------------------

export type AppGate = 'schema' | 'forbidden' | null;
let gate: AppGate = null;
const listeners = new Set<() => void>();

export function setAppGate(next: AppGate) {
  if (gate === next) return;
  gate = next;
  listeners.forEach((l) => l());
}
export function useAppGate(): AppGate {
  return useSyncExternalStore(
    (l) => {
      listeners.add(l);
      return () => listeners.delete(l);
    },
    () => gate,
    () => gate,
  );
}

/** Which gate, if any, an error should open. */
export function gateFor(err: unknown): AppGate {
  const code = (err as { code?: unknown } | null)?.code;
  if (code === 'PGRST106') return 'schema';
  if (code === '42501') return 'forbidden';
  return null;
}

function isFatal(err: unknown): boolean {
  if (gateFor(err)) return true;
  if (err instanceof ApiError) return err.status >= 400 && err.status < 500;
  const code = (err as { code?: unknown } | null)?.code;
  // PostgREST client errors (PGRST1xx/2xx/3xx) and Postgres errors other than connection
  // problems will not get better on retry.
  return typeof code === 'string' && code !== '' && !/^08|^57|^53/.test(code);
}

export const queryClient = new QueryClient({
  queryCache: new QueryCache({
    onError: (err) => {
      const g = gateFor(err);
      if (g) setAppGate(g);
    },
  }),
  mutationCache: new MutationCache({
    onError: (err) => {
      // A refused write is shown next to the control that made it; only a missing schema is global.
      if (gateFor(err) === 'schema') setAppGate('schema');
    },
  }),
  defaultOptions: {
    queries: {
      staleTime: 60_000,
      gcTime: 10 * 60_000,
      refetchOnWindowFocus: false,
      retry: (count, err) => !isFatal(err) && count < 2,
      retryDelay: (n) => Math.min(500 * 2 ** n, 4000),
    },
  },
});

// ---- Reads ------------------------------------------------------------------------------------

/**
 * The `data` type of a supabase-js response's success branch. For a plain `{data, error}` object
 * (a custom async fn) whose error may be null, it is that object's `data` type.
 */
export type SbData<R> = R extends { error: null; data: infer D }
  ? D
  : R extends { error: infer E; data: infer D }
    ? null extends E
      ? D
      : never
    : never;
type SbResponse = { data: unknown; error: unknown };
export type SbQueryOptions = { enabled?: boolean; staleTime?: number; refetchInterval?: number | false };

/**
 * `useSb(key, () => supabase.from('brands').select('*'))` → UseQueryResult with the row type
 * inferred. Throws `SbError` on error. An explicit type also works:
 * `useSb<{ id: string }[]>(key, () => …)`.
 */
export function useSb<R extends SbResponse>(
  key: unknown[],
  fn: () => PromiseLike<R>,
  opts?: SbQueryOptions,
): UseQueryResult<SbData<R>, SbError>;
export function useSb<T>(
  key: unknown[],
  fn: () => PromiseLike<{ data: T | null; error: any }>,
  opts?: SbQueryOptions,
): UseQueryResult<T, SbError>;
export function useSb(
  key: unknown[],
  fn: () => PromiseLike<SbResponse>,
  opts: SbQueryOptions = {},
): UseQueryResult<any, SbError> {
  return useQuery<unknown, SbError>({
    queryKey: key,
    queryFn: async () => {
      const { data, error } = await fn();
      if (error) throw toSbError(error);
      return data ?? null;
    },
    ...opts,
  });
}

/** Awaits a supabase-js call and returns its data, or throws `SbError`. For mutations. */
export async function unwrap<R extends SbResponse>(p: PromiseLike<R>): Promise<SbData<R>> {
  const { data, error } = await p;
  if (error) throw toSbError(error);
  return data as SbData<R>;
}

/** GET (no body) or POST to `seo-api` as a query. */
export function useApi<T>(key: unknown[], path: string, body?: unknown, opts: SbQueryOptions = {}) {
  return useQuery<T, ApiError>({
    queryKey: ['api', ...key],
    queryFn: ({ signal }) => callApi<T>(path, body, { signal }),
    ...opts,
  });
}

/** A short, plain sentence for any error this app throws. */
export function errorMessage(err: unknown): string {
  if (!err) return '';
  if (err instanceof ApiError) {
    if (err.status === 0) return 'Could not reach the server. Check the connection and try again.';
    if (err.status === 401) return 'Your session ended. Sign in again.';
    if (err.status === 403) return 'Your role on this brand does not allow that.';
    return err.message;
  }
  const code = (err as { code?: unknown }).code;
  if (code === '42501') return 'Your role on this brand does not allow that.';
  return (err as Error).message || 'Something went wrong.';
}
